import { Typography, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { PrintReceiptPosCrumbs } from 'front-office/constants/print-receipt-pos-crumbs';
import { Fragment, ReactElement, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Filter from 'front-office/components/print-receipt-pos/filter';
import TablePrintReceiptPos from 'front-office/components/print-receipt-pos/table';
import {
  validationSchemaFilter,
  validationSchemaTable,
} from 'front-office/constants/print-receipt-pos-schema';
import {
  FiltersFormPrintReceiptPos,
  FormPrintReceiptPos,
} from 'front-office/view-models/print-receipt-pos-page';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { ResponsePrintReceiptPosData } from 'shell/models/print-receipt-pos-data';
import { transformerPrintReceiptPosData } from 'front-office/utils/transformer-print-receipt-pos-data';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { transformerPrintReceiptPosRequest } from 'front-office/utils/transformer-print-receipt-pos-request';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
// import { openPdf } from 'shell/utils/open-pdf';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ReleasedService } from 'shell/models/certificate';
import ModalPrintSuccess from 'front-office/components/shared/success-print-pos';
import { DateTime } from 'luxon';

const PrintReceiptPosPage = (): ReactElement => {
  const [list, getList] = useHttpFetch<
    HttpResponseList<ResponsePrintReceiptPosData>,
    FormPrintReceiptPos
  >(`${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati/senza-ricevuta`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerPrintReceiptPosData,
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '', receipts: [] });

  const [modalPrintSuccess, setModalPrintSuccess] = useState({ receipts: [], open: false });

  const getPrintReceiptPos = useHttpClient(
    `${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati/stampa-ricevuta-pos`,
    {
      transformer: (response) => response.data,
      options: {
        method: 'POST',
      },
    }
  );

  const saveCertificate = useHttpClient(
    `${__CERTIFICATION_API_URL__}/certificazione/registro-doc-rilasciati/aggiorna-certificati-ricevuta-pos`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const { userProfile } = useUserProfile();

  const initialValuesFilter: FiltersFormPrintReceiptPos = useMemo(() => {
    const codeControlloCassa = userProfile.datiLogin?.profile?.feature?.controlloDiCassa || '';

    const isEnabledComboService = !(
      codeControlloCassa === '0' ||
      codeControlloCassa === '1' ||
      !codeControlloCassa
    );

    const isEnabledComboOperator = !(codeControlloCassa === '0' || !codeControlloCassa);

    return {
      dateFrom: DateTime.now(),
      dateTo: DateTime.now(),
      page: 0,
      rowsPerPage: 10,
      service: isEnabledComboService
        ? ''
        : userProfile.datiLogin?.profile?.servizio?.descrizione || '', // fixed
      operator: isEnabledComboOperator ? '' : userProfile.datiLogin?.nome || '', // fixed
      serviceCode: isEnabledComboService
        ? ''
        : userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio || '',
      operatorCode: isEnabledComboOperator ? '' : userProfile.datiLogin?.key?.userName || '',
    };
  }, [userProfile.datiLogin]);

  const onSubmitFilter = useEventCallback((values: FiltersFormPrintReceiptPos) => {
    let service = '';

    if (values?.serviceCode) {
      service = values?.serviceCode;
    } else if (!values?.service || values?.service === 'TUTTI') {
      service = '';
    } else {
      service = values?.service;
    }

    let operator = '';

    if (values?.operatorCode) {
      operator = values?.operatorCode;
    } else if (!values?.operator || values?.operator === 'ALL') {
      operator = '';
    } else {
      operator = values?.operator;
    }

    const data = {
      codiceOperatoreRilascio: operator || undefined,
      codiceServizioRilascio: service || undefined,
      rilascioDal: values.dateFrom?.toFormat('yyyy-MM-dd') || undefined,
      rilascioAl: values.dateTo?.toFormat('yyyy-MM-dd') || undefined,
    };

    getList({ data });
  });

  const onSubmitTable = useEventCallback((values: FormPrintReceiptPos) => {
    const data = transformerPrintReceiptPosRequest(values || {});
    (
      getPrintReceiptPos({ data }) as unknown as ResponsePromise<
        HttpResponse<
          ResponseCertificateGeneric & { certificatiRilasciati: Array<ReleasedService> }
        >,
        HttpResponse<ResponseCertificateGeneric & { certificatiRilasciati: Array<ReleasedService> }>
      >
    ).then((response) => {
      const responseCasted = response as HttpResponse<
        ResponseCertificateGeneric & { certificatiRilasciati: Array<ReleasedService> }
      >;
      const pdfCertificate = responseCasted?.payload?.contenutoPdf;

      // stampa pdf
      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({
          data: pdfCertificate,
          name: 'Ricevuta_POS',
          open: true,
          receipts: responseCasted?.payload?.certificatiRilasciati as never[],
        });
      }

      toast.success('Operazione eseguita con successo');
    });
  });

  const onYes = useEventCallback((values: FiltersFormPrintReceiptPos) => {
    //CALL TO BACKEND
    (
      saveCertificate({
        data: modalPrintSuccess.receipts,
      }) as ResponsePromise<void>
    ).then(() => {
      // close ModalPrintSuccess
      setModalPrintSuccess({ open: false, receipts: [] });
      const data = {
        codiceOperatoreRilascio: values.operator || undefined,
        codiceServizioRilascio: values.service || undefined,
        rilascioDal: values.dateFrom?.toFormat('yyyy-MM-dd') || undefined,
        rilascioAl: values.dateTo?.toFormat('yyyy-MM-dd') || undefined,
      };
      //THEN REFRESH LIST
      getList({ data });
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={PrintReceiptPosCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Stampa ricevuta POS
        </Typography>
      </div>
      <Formik
        validationSchema={validationSchemaFilter}
        initialValues={initialValuesFilter as FiltersFormPrintReceiptPos}
        onSubmit={onSubmitFilter}
      >
        {({ values }) => (
          <Form>
            <Filter />
            <ModalPrintSuccess
              open={modalPrintSuccess.open}
              onClose={() => {
                setModalPrintSuccess({ open: false, receipts: [] });
              }}
              onYes={() => onYes(values)}
            />
          </Form>
        )}
      </Formik>
      <Formik
        validationSchema={validationSchemaTable}
        initialValues={(list.data || { selected: [], list: [] }) as FormPrintReceiptPos}
        onSubmit={onSubmitTable}
      >
        <Form>
          <TablePrintReceiptPos
            data={(list.data || { selected: [], list: [] }) as FormPrintReceiptPos}
          />
          <ModalViewerPDF
            open={pdfModal.open}
            data={pdfModal.data}
            name={pdfModal.name}
            title="Stampa Ricevuta POS"
            onClose={() => {
              setPdfModal({ data: '', open: false, name: '', receipts: [] });

              setModalPrintSuccess({ open: true, receipts: pdfModal.receipts });
            }}
          />
        </Form>
      </Formik>
    </Fragment>
  );
};

PrintReceiptPosPage.displayName = 'Print Receipt Pos Page component';

export default PrintReceiptPosPage;
